import { type FC, type MouseEvent, useState } from "react";
import { HeaderCellTitle, TableSortLabel } from "~/common/components/Table/TableElements";

import { SortDirection, useSorting } from "~/common/hooks/useSorting";

import { type AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { Col, type ColSizes, Row } from "~/common/components/Layout";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type EmailOrderQuoteData } from "../types";
import {
  userCanSetMyCompanyOrders,
  userCanSetMyCompanyQuotes,
} from "~/features/myAccount/dashboard/helper/permissions";
import { EmailsOrderListTableRow } from "./EmailsOrderListTableRow";
import { useTranslation } from "react-i18next";

enum SortKinds {
  Email,
}

const SORT_HANDLERS: Dict<(a: EmailOrderQuoteData, b: EmailOrderQuoteData) => number> = {
  [SortKinds.Email]: (a, b) => a.recipientEmail.localeCompare(b.recipientEmail),
};

const headerStrings = ["email", "orderAcknowledgement", "quoteAcknowledgement"];
const headerSorting = [SortKinds.Email];

interface EmailsOrderListTableProps {
  data: EmailOrderQuoteData[];
  availableAccounts: AccountInfoData[];
  onRowEdit?: (data: EmailOrderQuoteData) => void;
  onRowUpdate?: (data: EmailOrderQuoteData) => void;
  onRowDelete?: (data: EmailOrderQuoteData) => void;
}

export const EmailsOrderListTable: FC<EmailsOrderListTableProps> = ({
  data,
  availableAccounts,
  onRowEdit,
  onRowUpdate,
  onRowDelete,
}) => {
  const { t } = useTranslation();
  const canSetMyCompanyOrders = userCanSetMyCompanyOrders();
  const canSetMyCompanyQuotes = userCanSetMyCompanyQuotes();
  const headerSizes = canSetMyCompanyOrders !== canSetMyCompanyQuotes ? [4, 6, 6, 2] : [2, 4, 4, 2];

  const [sortCode, setSortCode] = useState(SortKinds.Email);
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);

  const rows = useSorting<EmailOrderQuoteData>(data, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: SortKinds) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: SortKinds) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const headerT = (key: string) => t(`page.orderQuoteSettings.table.header.${key}`);

  return (
    <>
      <div className={"scha__order-quote-settings__main__table"}>
        <Row className={"scha__order-quote-settings__main__table__header"}>
          {headerStrings &&
            headerStrings.map((header, index) => {
              if (
                (!canSetMyCompanyOrders && headerStrings[index] === "orderAcknowledgement") ||
                (!canSetMyCompanyQuotes && headerStrings[index] === "quoteAcknowledgement")
              ) {
                return null;
              }
              return (
                <Col
                  key={index}
                  size={headerSizes[index] as ColSizes}
                  className={
                    "scha__order-quote-settings__main__table__cols__" + headerStrings[index] + "__header col-header"
                  }
                >
                  {headerSorting[index] !== undefined ? (
                    <TableSortLabel {...createTableProps(headerSorting[index])}>
                      <HeaderCellTitle header={headerT(header)} />
                    </TableSortLabel>
                  ) : (
                    <TableSortLabel active={false} disabled={true} hideSortIcon={true}>
                      <HeaderCellTitle header={headerT(header)} />
                    </TableSortLabel>
                  )}
                </Col>
              );
            })}
          <Col size={1} className={"scha__order-quote-settings__main__table__cols__edit__header col-header"}></Col>
          <Col size={1} className={"scha__order-quote-settings__main__table__cols__delete__header col-header"}></Col>
        </Row>
        <div className="scha__order-quote-settings__main__table__body">
          {rows &&
            rows.map((entry, index) => (
              <Card key={index}>
                <EmailsOrderListTableRow
                  headerSizes={headerSizes}
                  headerStrings={headerStrings}
                  entry={entry}
                  availableAccounts={availableAccounts}
                  onRowDelete={onRowDelete}
                  onRowEdit={onRowEdit}
                  onRowUpdate={onRowUpdate}
                />
              </Card>
            ))}
        </div>
      </div>
    </>
  );
};
